<template>
  <el-dialog
    :title="$t('parentOrTeacher.bindingStudent')"
    :visible.sync="visible"
    width="60%"
    @close="close"
    class="bindingStudent"
  >
    <el-form label-width="200px" style="margin-top: 24px">
      <el-row>
        <el-col :span="15">
          <el-form-item prop="username" :label="$t('parentOrTeacher.username')">
            <el-input v-model="username" />
          </el-form-item>
        </el-col>
        <el-button
          style="margin-left: 24px"
          type="primary"
          @click="getStudentList"
          >{{ $t("parentOrTeacher.doQuery") }}
        </el-button>
        <el-button style="margin-left: 24px" @click="reset"
          >{{ $t("parentOrTeacher.reset") }}
        </el-button>
        <el-button class="btn-create-student" type="primary" plain @click="openCreateStudentDialog">
          Create students
        </el-button>
      </el-row>
    </el-form>
    <div class="content">
      <div
        @click="selectBindingStudent(item)"
        v-for="item in studentList"
        :key="item.id"
        :class="selectBindingStudentId === item.id ? 'selected' : ''"
        class="card"
      >
        <div class="avatar-name" v-if="!item.avatarUrl">
          {{ item.username.charAt(0) }}
        </div>
        <div v-else class="avatar-avatarUrl">
          <img
            :src="item.avatarUrl"
            alt=""
          />
        </div>
        <div class="name">
          {{ item.username }}
        </div>
        <div class="userInfo">
          <div class="info" v-for="it in userInfoList" :key="it.value">
            <span class="value">{{ it.key }}：</span>
            <span class="label">{{ item[it.value] }}</span>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer">
      <div class="binding-footer">
        <label v-if="canActivateOnBind" class="activate-license-option">
          <input v-model="activateOnBind" type="checkbox" />
          <span class="activate-license-copy">
            <strong>Activate annual license for this student</strong>
            <small>Uses one purchased teacher seat after binding.</small>
          </span>
        </label>
        <div class="binding-footer-actions">
          <el-button type="primary" @click="submit">{{
            $t("parentOrTeacher.Binding")
          }}</el-button>
          <el-button @click="close">{{ $t("parentOrTeacher.Cancel") }}</el-button>
        </div>
      </div>
    </div>
    <el-dialog
      title="Create Student"
      :visible.sync="createStudentVisible"
      width="600px"
      custom-class="create-student-dialog-panel"
      append-to-body
      @close="resetCreateStudentForm"
    >
      <el-form
        ref="createStudentForm"
        :model="createStudentForm"
        :rules="createStudentRules"
        class="create-student-form"
        label-width="160px"
      >
        <el-form-item label="Grade" prop="grade">
          <el-select v-model="createStudentForm.grade" placeholder="Select Grade" style="width: 100%">
            <el-option :label="$t('tutor-register.gradeEight')" value="8" />
            <el-option :label="$t('tutor-register.gradeNine')" value="9" />
            <el-option :label="$t('tutor-register.gradeTen')" value="10" />
            <el-option :label="$t('tutor-register.gradeEleven')" value="11" />
            <el-option :label="$t('tutor-register.gradeTwelve')" value="12" />
          </el-select>
        </el-form-item>
        <el-form-item label="Username" prop="userAccount">
          <el-input v-model="createStudentForm.userAccount" />
        </el-form-item>
        <el-form-item label="Preferred Name" prop="username">
          <el-input v-model="createStudentForm.username" />
        </el-form-item>
        <el-form-item label="Gender" prop="sex">
          <el-select v-model="createStudentForm.sex" style="width: 100%">
            <el-option :label="$t('tutor-register.male')" value="1" />
            <el-option :label="$t('tutor-register.female')" value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="Nationality" prop="country">
          <el-input v-model="createStudentForm.country" />
        </el-form-item>
        <el-form-item label="Password" prop="password">
          <el-input v-model="createStudentForm.password" show-password type="password" />
        </el-form-item>
        <el-form-item label="Confirm Password" prop="rePassword">
          <el-input v-model="createStudentForm.rePassword" show-password type="password" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button type="primary" :loading="createStudentLoading" @click="handleCreateStudent">Create & Bind</el-button>
        <el-button @click="createStudentVisible = false">Cancel</el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>
<script>
import { students, bind } from '@/api/tutor/parentOrTeacher'
import { register } from '@/api/tutor/index'
import { existAccount } from '@/api/user'
import { encrypt } from '@/utils/crypto'
import { mapState } from 'vuex'

export default {
  name: 'AddBinding',
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    isTeacherUser () {
      const type = String(this.userInfo?.type ?? '')
      return type === '1' || Boolean(this.userInfo?.role && this.userInfo.role !== 'S' && this.userInfo.role !== 'P')
    },
    canActivateOnBind () {
      return this.isTeacherUser
    }
  },
  data () {
    const validateStudentAccountUnique = async (rule, value, callback) => {
      if (!value) return callback()
      try {
        const res = await existAccount({ userAccount: value })
        if (res.code === 200) {
          callback()
        } else {
          callback(new Error(this.$t('validation.accountExists')))
        }
      } catch (e) {
        callback(new Error(this.$t('validation.accountVerifyFailed')))
      }
    }
    const validateCreatePasswordRepeat = (rule, value, callback) => {
      if (value !== this.createStudentForm.password) {
        callback(new Error(this.$t('validation.passVerifyFailed')))
      } else {
        callback()
      }
    }
    return {
      visible: false,
      username: '',
      studentList: [],
      selectBindingStudentId: '',
      activateOnBind: false,
      createStudentVisible: false,
      createStudentLoading: false,
      createStudentForm: {
        grade: '',
        userAccount: '',
        username: '',
        sex: '1',
        country: '',
        password: '',
        rePassword: ''
      },
      createStudentRules: {
        grade: [
          {
            required: true,
            message: this.$t('validation.enterGrade'),
            trigger: 'change'
          }
        ],
        userAccount: [
          {
            required: true,
            message: this.$t('validation.enterUserAccount'),
            trigger: 'blur'
          },
          {
            min: 3,
            max: 20,
            message: this.$t('validation.length3to20'),
            trigger: 'blur'
          },
          {
            validator: validateStudentAccountUnique,
            trigger: 'blur'
          }
        ],
        username: [
          {
            required: true,
            message: this.$t('validation.enterUsername'),
            trigger: 'blur'
          }
        ],
        sex: [
          {
            required: true,
            message: this.$t('validation.selectGender'),
            trigger: 'change'
          }
        ],
        country: [
          {
            required: true,
            message: this.$t('validation.enterNationality'),
            trigger: 'blur'
          }
        ],
        password: [
          {
            required: true,
            message: this.$t('validation.enterPassword'),
            trigger: 'blur'
          },
          {
            min: 6,
            message: this.$t('validation.passwordMin6'),
            trigger: 'blur'
          }
        ],
        rePassword: [
          {
            required: true,
            message: this.$t('validation.enterPasswordAgain'),
            trigger: 'blur'
          },
          {
            validator: validateCreatePasswordRepeat,
            trigger: 'blur'
          }
        ]
      },
      userInfoList: [
        { key: this.$t('parentOrTeacher.grade'), value: 'grade' },
        { key: this.$t('parentOrTeacher.country'), value: 'country' },
        { key: this.$t('parentOrTeacher.tel'), value: 'tel' },
        { key: this.$t('parentOrTeacher.email'), value: 'email' }
      ]
    }
  },
  methods: {
    /**
     * 打开学生注册弹窗
     */
    openCreateStudentDialog () {
      this.createStudentVisible = true
    },
    /**
     * 重置学生注册表单
     */
    resetCreateStudentForm () {
      this.createStudentForm = {
        grade: '',
        userAccount: '',
        username: '',
        sex: '1',
        country: '',
        password: '',
        rePassword: ''
      }
      if (this.$refs.createStudentForm) {
        this.$refs.createStudentForm.clearValidate()
      }
    },
    show () {
      this.visible = true
      this.getStudentList()
    },
    reset () {
      this.username = ''
      this.getStudentList()
    },
    getStudentList () {
      const params = {
        username: this.username
      }
      students(params).then((res) => {
        if (res.code !== 200) {
          return false
        }
        this.studentList = res.data
      })
    },
    close () {
      this.selectBindingStudentId = ''
      this.activateOnBind = false
      this.visible = false
      this.createStudentVisible = false
    },
    selectBindingStudent (item) {
      this.selectBindingStudentId =
        item.id === this.selectBindingStudentId ? '' : item.id
    },
    submit () {
      if (!this.selectBindingStudentId) {
        this.$message.error(this.$t('parentOrTeacher.PleaseSelect'))
        return false
      }
      this.bindStudent(this.selectBindingStudentId, this.shouldActivateOnBind())
    },
    /**
     * 执行绑定学生
     * @param {number|string} studentId 学生ID
     * @returns {Promise<void>}
     */
    async bindStudent (studentId, activate = false) {
      const guardianId = this.userInfo.id
      const guardianType = this.isTeacherUser ? '1' : '0'
      const res = await bind(guardianId, guardianType, studentId, {
        activate,
        relation: this.isTeacherUser ? 'Teacher' : 'Guardian',
        operator: this.userInfo.username || this.userInfo.userAccount || 'teacher'
      })
      if (res.code !== 200) {
        return
      }
      this.$message.success(activate ? 'Student bound and annual license activated' : this.$t('parentOrTeacher.BindingSuccess'))
      this.$emit('submit')
      this.close()
    },
    shouldActivateOnBind () {
      return this.canActivateOnBind && this.activateOnBind
    },
    /**
     * 注册学生并自动绑定
     */
    handleCreateStudent () {
      this.$refs.createStudentForm.validate(async (valid) => {
        if (!valid) return false
        this.createStudentLoading = true
        try {
          const params = {
            grade: this.createStudentForm.grade,
            userAccount: this.createStudentForm.userAccount,
            username: this.createStudentForm.username,
            sex: this.createStudentForm.sex,
            country: this.createStudentForm.country,
            password: encrypt(this.createStudentForm.password)
          }
          const res = await register(params)
          if (res.code !== 200) {
            this.$message.error(res.message || this.$t('tutor-register.registerFailed'))
            return
          }
          let studentId = res?.data?.id || res?.data?.userId
          if (!studentId) {
            const listRes = await students({ username: this.createStudentForm.userAccount })
            if (listRes.code === 200 && Array.isArray(listRes.data)) {
              const exact = listRes.data.find((item) => item.userAccount === this.createStudentForm.userAccount || item.username === this.createStudentForm.userAccount)
              studentId = exact?.id
            }
          }
          if (!studentId) {
            this.$message.error('Create success but failed to get student id.')
            return
          }
          this.createStudentVisible = false
          this.$message.success(this.$t('tutor-register.registerSuccess'))
          await this.bindStudent(studentId, this.shouldActivateOnBind())
          this.getStudentList()
        } finally {
          this.createStudentLoading = false
        }
      })
    }
  }
}
</script>

<style scoped>
:deep.el-dialog__wrapper.bindingStudent .el-dialog{
  border-radius: 8px;
}
:deep.el-dialog__wrapper.bindingStudent .el-dialog__header{
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  padding: 20px;
  border-radius: 8px 8px 0 0;
}
:deep.el-dialog__wrapper.bindingStudent .el-dialog__title{
  font-weight: bold;
  font-size: 26px;
  color: #ffffff;
}
:deep.el-dialog__wrapper.bindingStudent .el-dialog__body {
  padding: 0;
}

.content {
  margin: 0 12px;
  padding: 0 0 24px;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  max-height: 516px;

  .card {
    width: calc(100% / 3 - 24px);
    margin: 2px 12px 22px 12px;
    height: 148px;
    background: #ffffff;
    padding: 10px;
    display: flex;
    align-items: center;
    flex-direction: column;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    &:hover{
      transform: translateY(-4px);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
    }
    &.selected {
      box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.15),
        inset 0px 0px 10px 1px #6cbfff;
      border: 2px solid #0d47a1;
    }
    .avatar-name {
      width: 37px;
      height: 37px;
      font-size: 24px;
      font-weight: bold;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: #000000;
      color: #fff;
    }
    .avatar-avatarUrl{
      img{
        width: 37px;
        height: 37px;
        border-radius: 50%;
      }
    }
    .name {
      margin: 10px 0 0;
      height: 24px;
      font-weight: bold;
      font-size: 18px;
      color: #000000;
    }
    .userInfo {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      .info {
        width: 50%;
        margin-top: 6px;
        display: flex;
        font-size: 14px;
        color: #afafaf;
        .value {
          width: 80px;
          display: inline-block;
          text-align: right;
        }
        .label {
          width: calc(100% - 80px);
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
}

.btn-create-student {
  margin-left: 24px !important;
}

.binding-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.binding-footer-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.activate-license-option {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 420px;
  padding: 10px 12px;
  border: 1px solid #ddd6fe;
  border-radius: 12px;
  cursor: pointer;
  background: #f8f7ff;
  text-align: left;
}

.activate-license-option input {
  width: 16px;
  height: 16px;
  margin-top: 3px;
  accent-color: #7c3aed;
  cursor: pointer;
}

.activate-license-copy {
  display: grid;
  gap: 2px;
}

.activate-license-copy strong {
  color: #312e81;
  font-size: 14px;
  line-height: 1.25;
}

.activate-license-copy small {
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
}

/* Create Student 弹窗：参考注册页风格 */
:deep(.create-student-dialog-panel) {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
}

:deep(.create-student-dialog-panel .el-dialog__header) {
  padding: 18px 20px;
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
}

:deep(.create-student-dialog-panel .el-dialog__title) {
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
}

:deep(.create-student-dialog-panel .el-dialog__headerbtn .el-dialog__close) {
  color: #ffffff;
  font-weight: 700;
}

:deep(.create-student-dialog-panel .el-dialog__body) {
  padding: 22px 22px 10px;
  background: linear-gradient(180deg, #f3f0ff 0%, #f9fafc 46%, #ffffff 100%);
}

:deep(.create-student-dialog-panel .el-dialog__footer) {
  padding: 0 22px 22px;
  background: #ffffff;
}

.create-student-form :deep(.el-form-item) {
  margin-bottom: 20px;
}

.create-student-form :deep(.el-form-item__label) {
  font-weight: 700;
  color: #1f2937;
}

.create-student-form :deep(.el-input__inner) {
  height: 44px;
  line-height: 44px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
}

.create-student-form :deep(.el-input__inner:focus) {
  border-color: #8b5cf6;
}

.create-student-form :deep(.el-select .el-input__inner) {
  height: 44px;
  line-height: 44px;
}

:deep(.create-student-dialog-panel .el-dialog__footer .el-button--primary) {
  border: none;
  border-radius: 999px;
  padding: 12px 24px;
  font-weight: 700;
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
}

:deep(.create-student-dialog-panel .el-dialog__footer .el-button--default) {
  border-radius: 999px;
  padding: 12px 24px;
  font-weight: 700;
}
</style>
